import { Department, HoursOfOperation } from '../typings/main';

export interface ScheduleResult {
  closed: boolean;
  scheduleInfo: string;
  labelKey: string;
}

/**
 * Converts 24-hour time string to 12-hour or 24-hour format based on locale
 * @param time - Time string in "HH:MM" format (e.g., "07:30", "18:00")
 * @param locale - Locale string ('en' for 12-hour, 'fr' for 24-hour format)
 * @returns Formatted time (e.g., "7:30AM", "6PM" for en, "18:00" for fr)
 */
const formatTime = (time: string, locale: string = 'en'): string => {
  const [hours, minutes] = time.split(':').map(Number);

  if (locale === 'fr') {
    if (minutes === 0) {
      return `${hours.toString().padStart(2, '0')}h`;
    }
    return `${hours.toString().padStart(2, '0')}h${minutes.toString().padStart(2, '0')}`;
  }

  const period = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12; // Convert 0 to 12 for midnight

  // Only show minutes if they're not :00
  if (minutes === 0) {
    return `${hour12}${period}`;
  }

  return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

/**
 * Converts time string to minutes since midnight for easy comparison
 * @param time - Time string in "HH:MM" format
 * @returns Total minutes since midnight
 */
const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Gets the current time in minutes since midnight
 */
const getCurrentTimeInMinutes = (timeOffset: number = 0): number => {
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes() + timeOffset;
};

/**
 * Gets next day's name
 */
const getNextDayName = (): string => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toLocaleDateString('en-US', { weekday: 'long' });
};

/**
 * Finds a department by code or description
 * @param departments - Array of departments
 * @param departmentIdentifier - Department code (e.g., 'SVC') or description (e.g., 'Service')
 * @returns Found department or null
 */
const findDepartment = (
  departments: Department[],
  departmentIdentifier: string,
): Department | null => {
  // First try to find by exact departmentCode match
  let department = departments.find(
    (dept: Department) => dept.departmentCode === departmentIdentifier,
  );

  // If not found by code, try by description (case-insensitive partial match)
  if (!department) {
    department = departments.find((dept: Department) =>
      dept.description
        .toLowerCase()
        .includes(departmentIdentifier.toLowerCase()),
    );
  }

  return department || null;
};

/**
 * Gets the opening hours for the next day for a specific department
 * @param departments - Array of departments
 * @param departmentIdentifier - Department code or description to search for
 * @returns Next day's opening time or null
 */
const getNextDayOpenTime = (
  departments: Department[],
  departmentIdentifier: string = 'Service',
): string | null => {
  const department = findDepartment(departments, departmentIdentifier);

  if (!department) return null;

  const nextDay = getNextDayName();
  const nextDayHours = department.hoursOfOperation.find(
    (item: HoursOfOperation) => item.name === nextDay,
  );

  if (!nextDayHours || nextDayHours.closedIndicator === 'Y') {
    return null;
  }

  return nextDayHours.hours?.[0]?.openTime || null;
};

/**
 * Determines if a specific department is currently open and returns schedule info
 * @param departments - Array of dealer departments
 * @param locale - Locale string ('en' for 12-hour, 'fr' for 24-hour format)
 * @param departmentIdentifier - Department code (e.g., 'SVC') or description (e.g., 'Service'). Defaults to 'Service'
 * @param timeOffset - Time offset in minutes for testing purposes
 * @returns Schedule information object or null if department not found
 */
export const getOpenSchedule = (
  departments: Department[],
  locale: string = 'en',
  departmentIdentifier: string = 'Service',
  timeOffset: number = 0,
): ScheduleResult | null => {
  // Find the specified department (defaults to Service for backward compatibility)
  const targetDepartment = findDepartment(departments, departmentIdentifier);

  // Return null if department not found (very rare case as mentioned)
  if (!targetDepartment) {
    console.warn(
      `Department '${departmentIdentifier}' not found. Available departments:`,
      departments.map((d) => `${d.departmentCode} (${d.description})`),
    );
    return null;
  }

  const currentDay = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
  });

  const todaySchedule = targetDepartment.hoursOfOperation.find(
    (item: HoursOfOperation) => item.name === currentDay,
  );

  if (!todaySchedule) return null;

  // Check if closed all day
  if (
    todaySchedule.closedIndicator === 'Y' ||
    !todaySchedule.hours ||
    todaySchedule.hours.length === 0
  ) {
    const nextDayOpenTime = getNextDayOpenTime(
      departments,
      departmentIdentifier,
    );

    if (nextDayOpenTime) {
      return {
        closed: true,
        scheduleInfo: formatTime(nextDayOpenTime, locale),
        labelKey: 'manageDealer.dealerCard.OpensTomorrowAt.label',
      };
    }

    // Fallback if no tomorrow info available
    return {
      closed: true,
      scheduleInfo: '',
      labelKey: 'manageDealer.dealerCard.Closed.label',
    };
  }

  // Get opening hours (assuming first entry is main hours)
  const hours = todaySchedule.hours[0];
  if (!hours) return null;

  const currentTimeMinutes = getCurrentTimeInMinutes(timeOffset);
  const openTimeMinutes = timeToMinutes(hours.openTime);
  const closeTimeMinutes = timeToMinutes(hours.closeTime);

  // CASE 1: Currently OPEN (current time is between open and close)
  if (
    currentTimeMinutes >= openTimeMinutes &&
    currentTimeMinutes < closeTimeMinutes
  ) {
    return {
      closed: false,
      scheduleInfo: formatTime(hours.closeTime, locale),
      labelKey: 'manageDealer.dealerCard.ClosesAt.label',
    };
  }

  // CASE 2: Before opening (current time is before open time)
  if (currentTimeMinutes < openTimeMinutes) {
    return {
      closed: true,
      scheduleInfo: formatTime(hours.openTime, locale),
      labelKey: 'manageDealer.dealerCard.OpensAt.label',
    };
  }

  // CASE 3: After closing (current time is after close time)
  if (currentTimeMinutes >= closeTimeMinutes) {
    const nextDayOpenTime = getNextDayOpenTime(
      departments,
      departmentIdentifier,
    );

    if (nextDayOpenTime) {
      return {
        closed: true,
        scheduleInfo: formatTime(nextDayOpenTime, locale),
        labelKey: 'manageDealer.dealerCard.OpensTomorrowAt.label',
      };
    }

    // Fallback: show today's open time if tomorrow not available
    return {
      closed: true,
      scheduleInfo: formatTime(hours.openTime, locale),
      labelKey: 'manageDealer.dealerCard.OpensAt.label',
    };
  }

  return null;
};
